import type { TFunction } from 'i18next';
import type {
  BusinessProfile,
  BusinessProfileError,
  HealthServiceAnswer,
  OrganizationForm,
  RegisteredFacts,
  SavedBusinessProfile,
} from '../../../api/businessProfile';
import type { FieldError } from '../../WorkEnvironmentSurveyDetail/assessmentView';
import { dayAndTime } from '../../WorkEnvironmentSurveyDetail/riskView';

export const ORGANIZATION_FORMS: OrganizationForm[] = [
  'AS',
  'ASA',
  'ANS',
  'DA',
  'ENK',
  'SA',
  'STI',
  'NUF',
  'FLI',
  'PUBLIC',
];

export const HEALTH_SERVICE_ANSWERS: HealthServiceAnswer[] = ['YES', 'NO', 'NOT_REQUIRED'];

type TextField = Exclude<
  keyof BusinessProfile,
  'organizationForm' | 'employeeCount' | 'hasCommittee' | 'healthService'
>;

export type BusinessForm = Record<TextField, string> & {
  organizationForm: OrganizationForm | '';
  employeeCount: string;
  hasCommittee: boolean | null;
  healthService: HealthServiceAnswer | null;
};

export const FIELD_IDS: Record<BusinessProfileError, string> = {
  orgNumberDigits: 'business-orgNumber',
  orgNumberChecksum: 'business-orgNumber',
  orgNumberTaken: 'business-orgNumber',
  legalName: 'business-legalName',
  employeeCount: 'business-employeeCount',
  ceoName: 'business-ceoName',
  committeeNextMeeting: 'business-committeeNextMeeting',
};

const TEXT_FIELDS: TextField[] = [
  'orgNumber',
  'legalName',
  'industryCode',
  'businessAddress',
  'postalCode',
  'postalPlace',
  'ceoName',
  'ceoEmail',
  'inspectionContactName',
  'inspectionContactRole',
  'inspectionContactEmail',
  'inspectionContactPhone',
  'committeeLeaderName',
  'committeeNextMeeting',
  'healthServiceName',
  'healthServiceContact',
];

export const businessForm = ({ orgNumber, profile }: SavedBusinessProfile): BusinessForm => ({
  ...(Object.fromEntries(TEXT_FIELDS.map((field) => [field, profile?.[field] ?? ''])) as Record<TextField, string>),
  orgNumber,
  organizationForm: profile?.organizationForm ?? '',
  employeeCount: profile?.employeeCount === null || !profile ? '' : String(profile.employeeCount),
  hasCommittee: profile?.hasCommittee ?? null,
  healthService: profile?.healthService ?? null,
});

const textOrNull = (value: string) => value.trim() || null;

const employeesOf = (value: string) => (value.trim() ? Number(value) : null);

export const toProfile = (form: BusinessForm): BusinessProfile => ({
  ...(Object.fromEntries(TEXT_FIELDS.map((field) => [field, textOrNull(form[field])])) as Record<
    TextField,
    string | null
  >),
  orgNumber: form.orgNumber.replace(/\s/g, ''),
  legalName: form.legalName.trim(),
  ceoName: form.ceoName.trim(),
  organizationForm: form.organizationForm || null,
  employeeCount: employeesOf(form.employeeCount),
  hasCommittee: form.hasCommittee,
  healthService: form.healthService,
});

const WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2];

/*
 * The API checks the organisation number too. The copy here gives feedback before the
 * lookup in the register, and is tested with the same cases.
 */
export const organizationNumberError = (value: string): BusinessProfileError | null => {
  const digits = value.replace(/\s/g, '');
  if (!/^\d{9}$/.test(digits)) return 'orgNumberDigits';
  const sum = WEIGHTS.reduce((total, weight, index) => total + weight * Number(digits[index]), 0);
  const remainder = 11 - (sum % 11);
  const checkDigit = remainder === 11 ? 0 : remainder;
  return checkDigit !== 10 && checkDigit === Number(digits[8]) ? null : 'orgNumberChecksum';
};

/** The required fields, checked before the form is sent */
export const formErrors = (form: BusinessForm): BusinessProfileError[] => [
  ...[organizationNumberError(form.orgNumber)].filter((error) => error !== null),
  ...(form.legalName.trim() ? [] : ['legalName' as const]),
  ...(form.ceoName.trim() ? [] : ['ceoName' as const]),
];

export const fieldErrors = (errors: BusinessProfileError[], t: TFunction): FieldError[] =>
  errors.map((error) => ({ fieldId: FIELD_IDS[error], message: t(`organizationBusiness.errors.${error}`) }));

export const errorFor = (errors: FieldError[], fieldId: string) =>
  errors.find((error) => error.fieldId === fieldId)?.message;

/** The register did not find the organisation number */
export const isNotRegistered = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status === 404;

/** The register has more organisation forms than Trustpulse offers. Then the chosen form is kept. */
export const withRegisteredFacts = (form: BusinessForm, facts: RegisteredFacts): BusinessForm => ({
  ...form,
  legalName: facts.legalName,
  organizationForm: facts.organizationForm ?? form.organizationForm,
  industryCode: facts.industryCode ?? '',
  businessAddress: facts.businessAddress ?? '',
  postalCode: facts.postalCode ?? '',
  postalPlace: facts.postalPlace ?? '',
});

export type Duty = 'safetyRep' | 'otherArrangement' | 'committee' | 'committeeOnRequest' | 'committeeMissing';

export const employeesIn = (form: Pick<BusinessForm, 'employeeCount'>) => Number.parseInt(form.employeeCount, 10) || 0;

/**
 * The limits in the Working Environment Act from 1 January 2024. Below the safety representative
 * limit the parties may agree in writing on another arrangement (§ 6-1). A committee is required
 * from the committee limit, and from the lower limit when one of the parties asks for it (§ 7-1).
 */
export const EMPLOYEE_LIMITS = { safetyRep: 5, committeeOnRequest: 10, committee: 30 };

/** Below the limit the parties may agree on another arrangement, so a safety representative is not required */
export const requiresSafetyRep = (employees: number) => employees >= EMPLOYEE_LIMITS.safetyRep;

const safetyRepDuties = (employees: number): Duty[] => {
  if (requiresSafetyRep(employees)) return ['safetyRep'];
  return employees > 0 ? ['otherArrangement'] : [];
};

const committeeDuties = (employees: number): Duty[] => {
  if (employees >= EMPLOYEE_LIMITS.committee) return ['committee'];
  return employees >= EMPLOYEE_LIMITS.committeeOnRequest ? ['committeeOnRequest'] : [];
};

/** What the Working Environment Act asks of a business of this size, sections 6-1 and 7-1 */
export const dutiesFor = (form: Pick<BusinessForm, 'employeeCount' | 'hasCommittee'>): Duty[] => {
  const employees = employeesIn(form);
  const missing: Duty[] =
    form.hasCommittee === false && employees >= EMPLOYEE_LIMITS.committee ? ['committeeMissing'] : [];
  return [...safetyRepDuties(employees), ...committeeDuties(employees), ...missing];
};

/** The duty in words, with the limit it follows from */
export const dutyText = (duty: Duty, employees: number, t: TFunction) =>
  t(`organizationBusiness.duties.${duty}`, { count: employees, limit: EMPLOYEE_LIMITS.safetyRep });

/** When the facts were fetched from the register at sign-up, as long as nobody has saved them */
export const fromSignupText = (saved: SavedBusinessProfile, t: TFunction, language: string) => {
  if (!saved.profile?.fromRegistry) return null;
  const date = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', { dateStyle: 'long' }).format(
    new Date(saved.profile.updatedAt),
  );
  return t('organizationBusiness.lookup.fromSignup', { date });
};

/** When the profile was saved, or what it is used for when it is not saved yet */
export const savedText = (updatedAt: string | null, now: Date, t: TFunction, language: string) => {
  if (!updatedAt) return t('organizationBusiness.notSaved');
  const when = dayAndTime(updatedAt, t, language, now);
  return t('organizationBusiness.saved', { when: when.charAt(0).toLocaleLowerCase(language) + when.slice(1) });
};
